import { Component, OnInit, ViewChild } from '@angular/core';
import { MatSnackBar } from '@angular/material';
import { Cache } from 'aws-amplify';
import { FilterParams } from '../../../core/filter-options/filter-options.service';
import { SelectOption } from '../../partials/content/general/searchable-select/searchable-select.component';
import { SegmentedOption } from '../../partials/content/general/segmented-control/segmented-control.component';
import {
  TabKey,
  FilterState,
  Recipe,
  Product
} from '../../../core/analytics';
import { DrinkVolumeTabComponent } from './tabs/drink-volume-tab/drink-volume-tab.component';
import { ConsumptionTabComponent } from './tabs/consumption-tab/consumption-tab.component';
import { ForecastTabComponent } from './tabs/forecast-tab/forecast-tab.component';
import { StockoutsTabComponent } from './tabs/stockouts-tab/stockouts-tab.component';
import {
  applyRoundedBarCorners,
  applyConcentricDonutPlugin,
  capitalizeRecipeName,
  DEFAULT_ALL_DRINKS,
  getBreakdownDisplayName as getBreakdownDisplayNameUtil,
  formatProductName as formatProductNameUtil,
  truncateLabel as truncateLabelUtil
} from '../../../core/analytics';
import { UserSettingsHttpService } from '../../../core/user-settings/_services/user-settings-http.service';
import { FilterOptionsService } from '../../../core/filter-options/filter-options.service';
import { AVERAGE_RANGE_REQUIREMENTS, MS_PER_DAY } from './analytics.constants';
import { MixpanelService } from '../../../services/mixpanel.service';

@Component({
  selector: 'kt-analytics',
  templateUrl: './analytics.component.html',
  styleUrls: ['./analytics.component.scss']
})
export class AnalyticsComponent implements OnInit {
  @ViewChild(DrinkVolumeTabComponent, { static: false }) drinkVolumeTab: DrinkVolumeTabComponent;
  @ViewChild(ConsumptionTabComponent, { static: false }) consumptionTab: ConsumptionTabComponent;
  @ViewChild(ForecastTabComponent, { static: false }) forecastTab: ForecastTabComponent;
  @ViewChild(StockoutsTabComponent, { static: false }) stockoutsTab: StockoutsTabComponent;

  activeTab: TabKey = 'drink-volume';
  forecastDays = 7;
  role: string = 'sadmin';

  filters: FilterState = {
    distributors: [],
    markets: [],
    clients: [],
    campuses: [],
    machines: [],
    products: [],
    drinks: [],
    saleScope: 'all',
    days: 7,
    datePreset: '7',
    breakdownBy: 'drink',
    avgBy: 'day',
    averageEnabled: false,
    compareEnabled: false,
  };

  // Local date properties for date pickers (Date objects)
  startDate: Date | null = null;
  endDate: Date | null = null;

  // Flag to track if custom dates have been initialized
  private customDatesInitialized = false;

  // Getter for start date max constraint (cannot be after end date or today)
  get maxStartDate(): Date | null {
    if (this.endDate) {
      return this.endDate;
    }
    return new Date(); // Can't select future dates
  }

  // Getter for end date min constraint (cannot be before start date)
  get minEndDate(): Date | null {
    return this.startDate;
  }

  // Getter for end date max constraint (cannot be beyond today)
  get maxEndDate(): Date {
    return new Date();
  }

  // Constants
  ALL_DRINKS: string[] = Array.from(DEFAULT_ALL_DRINKS); // Default fallback, will be populated from API
  drinkOptions: SelectOption[] = []; // Options for the searchable select dropdown
  productOptions: SelectOption[] = []; // Options for the product searchable select dropdown
  
  // Hierarchical filter options for export metadata
  distributorOptions: SelectOption[] = [];
  marketOptions: SelectOption[] = [];
  clientOptions: SelectOption[] = [];
  campusOptions: SelectOption[] = [];
  machineOptions: SelectOption[] = [];

  // Breakdown options based on active tab and user role
  get allowedBreakdowns(): FilterState['breakdownBy'][] {
    let breakdowns: FilterState['breakdownBy'][] = [];

    // Base breakdown options by tab (for super admin)
    if (this.activeTab === 'drink-volume') {
      breakdowns = ['drink', 'distributor', 'market', 'client', 'campus', 'machine'];
    } else if (this.activeTab === 'consumption') {
      breakdowns = ['product', 'distributor', 'market', 'client', 'campus', 'machine'];
    } else if (this.activeTab === 'forecast') {
      breakdowns = ['product', 'distributor', 'client', 'campus', 'machine'];
    }

    // Apply role-based restrictions
    if (this.role === 'sadmin') {
      // Super admin: all options available (no restrictions)
      return breakdowns;
    } else if (this.role === 'distiadmin') {
      // Distributor admin: all except 'distributor' (Partner)
      return breakdowns.filter(b => b !== 'distributor');
    } else if (this.role === 'marketadmin') {
      // Market admin: exclude 'distributor' and 'market', allow client/campus/machine/drink/product
      breakdowns = breakdowns.filter(b => b !== 'distributor' && b !== 'market');
      return breakdowns;
    } else if (this.role === 'clientadmin') {
      // Client admin: current availability is correct (drink/product + campus + machine)
      if (this.activeTab === 'drink-volume') {
        return ['drink', 'campus', 'machine'];
      } else if (this.activeTab === 'consumption') {
        return ['product', 'campus', 'machine'];
      } else if (this.activeTab === 'forecast') {
        return ['product', 'campus', 'machine'];
      }
    } else if (this.role === 'campusadmin') {
      // Campus admin: only machine & product/drink depending on the tab
      if (this.activeTab === 'drink-volume') {
        return ['drink', 'machine'];
      } else if (this.activeTab === 'consumption') {
        return ['product', 'machine'];
      } else if (this.activeTab === 'forecast') {
        return ['product', 'machine'];
      }
    }

    return breakdowns;
  }

  // Show/hide controls based on active tab
  get showProduct(): boolean {
    return this.activeTab !== 'drink-volume' && this.activeTab !== 'stockouts';
  }

  get showDrink(): boolean {
    return this.activeTab === 'drink-volume';
  }

  get showBreakdown(): boolean {
    return this.activeTab !== 'stockouts';
  }

  get showAverageControls(): boolean {
    return this.activeTab === 'drink-volume' || this.activeTab === 'consumption';
  }

  get showTimePreset(): boolean {
    return this.activeTab === 'drink-volume' || this.activeTab === 'consumption';
  }

  get showForecastRange(): boolean {
    return this.activeTab === 'forecast';
  }

  get showSaleScope(): boolean {
    return this.activeTab === 'drink-volume' && this.role === 'sadmin';
  }

  // Scope display values (keeping machine display for future use)
  get machineDisplay(): string {
    return this.filters.machines.length === 0 ? 'All Machines' :
           this.filters.machines.length <= 3 ? this.filters.machines.join(', ') :
           `${this.filters.machines.length} selected`;
  }

  get productDisplay(): string {
    return this.filters.products.length === 0 ? 'All Products' :
           this.filters.products.length <= 3 ? this.filters.products.join(', ') :
           `${this.filters.products.length} selected`;
  }

  get drinkDisplay(): string {
    return this.filters.drinks.length === 0 ? 'All Drinks' :
           this.filters.drinks.length <= 3 ? this.filters.drinks.join(', ') :
           `${this.filters.drinks.length} selected`;
  }

  get averageValue(): 'off' | 'day' | 'week' | 'month' {
    return this.filters.averageEnabled ? this.filters.avgBy : 'off';
  }

  get currentRangeDays(): number | null {
    if (this.filters.datePreset === 'custom') {
      return this.getCustomRangeDays();
    }

    return typeof this.filters.days === 'number' ? this.filters.days : null;
  }

  /**
   * Get filters for drink-volume-tab and consumption-tab with custom date parameters
   * when datePreset is 'custom'. When datePreset is not 'custom', excludes startDate and endDate.
   */
  get filtersWithCustomDates(): FilterState {
    if (this.filters.datePreset === 'custom') {
      return {
        ...this.filters,
        datePreset: 'custom',
        startDate: this.filters.startDate,
        endDate: this.filters.endDate
      };
    }
    // When not custom, return filters without startDate and endDate
    return {
      distributors: this.filters.distributors,
      markets: this.filters.markets,
      clients: this.filters.clients,
      campuses: this.filters.campuses,
      machines: this.filters.machines,
      products: this.filters.products,
      drinks: this.filters.drinks,
      saleScope: this.filters.saleScope,
      days: this.filters.days,
      datePreset: this.filters.datePreset,
      breakdownBy: this.filters.breakdownBy,
      avgBy: this.filters.avgBy,
      averageEnabled: this.filters.averageEnabled,
      compareEnabled: this.filters.compareEnabled
    };
  }

  // Helper method to get display name for breakdown options
  getBreakdownDisplayName(option: string): string {
    return getBreakdownDisplayNameUtil(option);
  }

  // Helper method to truncate labels longer than 20 characters
  truncateLabel(label: string, maxLength: number = 20): string {
    return truncateLabelUtil(label, maxLength);
  }

  // Helper method to format product names (capitalize and replace FV Cream, Beans)
  formatProductName(name: string): string {
    return formatProductNameUtil(name);
  }

  // Getter methods for segmented control options
  get breakdownOptions() {
    return this.allowedBreakdowns.map(option => ({
      label: this.getBreakdownDisplayName(option),
      value: option
    }));
  }

  get averageOptions(): SegmentedOption<'off' | 'day' | 'week' | 'month'>[] {
    return [
      { label: 'Off', value: 'off' },
      { label: 'Day', value: 'day' },
      {
        label: 'Week',
        value: 'week',
        disabled: !this.isAverageOptionAllowed('week')
      },
      {
        label: 'Month',
        value: 'month',
        disabled: !this.isAverageOptionAllowed('month')
      }
    ];
  }

  get datePresetOptions() {
    return [
      { label: '7d', value: '7' as const },
      { label: '30d', value: '30' as const },
      { label: '90d', value: '90' as const },
      { label: '1Y', value: '365' as const },
      { label: 'Custom', value: 'custom' as const }
    ];
  }

  get forecastOptions() {
    return [
      { label: 'Next Day', value: 1 },
      { label: 'Next Week', value: 7 },
      { label: 'Next Month', value: 30 }
    ];
  }

  get saleScopeOptions(): SegmentedOption<'all' | 'retail'>[] {
    return [
      { label: 'All', value: 'all' },
      { label: 'Retail', value: 'retail' }
    ];
  }

  constructor(
    private userSettingsHttpService: UserSettingsHttpService,
    private filterOptionsService: FilterOptionsService,
    private snackBar: MatSnackBar,
    private mixpanelService: MixpanelService
  ) {
    // Apply Chart.js customization for rounded bar corners
    applyRoundedBarCorners();
    // Apply plugin for concentric donut charts
    applyConcentricDonutPlugin();
  }

  ngOnInit() {
    const user = Cache.getItem('userAttr');
    if (user && user['custom:role']) {
      this.role = user['custom:role'];
    }

    // Track page view
    this.mixpanelService.track(this.mixpanelService.PAGE_VIEW);

    // Ensure breakdownBy is valid for the current role and active tab
    if (!this.allowedBreakdowns.includes(this.filters.breakdownBy)) {
      if (this.activeTab === 'drink-volume') {
        this.filters.breakdownBy = 'drink';
      } else if (this.activeTab === 'consumption') {
        this.filters.breakdownBy = 'product';
      } else if (this.activeTab === 'forecast') {
        this.filters.breakdownBy = 'product';
      }
    }

    // Initialize custom date range: 1 week ago to today
    this.initializeCustomDateRange();
    
    // Load hierarchical filter options for export metadata
    this.loadHierarchicalFilterOptions();

    // Initialize drinkOptions with default values (will be updated when API loads)
    this.drinkOptions = this.ALL_DRINKS.map((drink: string) => ({
      value: drink,
      label: drink
    }));
    // Load available recipes/drinks and products from API
    this.loadRecipes();
    this.loadProducts();

    // Initialize custom dates if datePreset is custom and active tab shows time preset
    if (this.filters.datePreset === 'custom' && this.showTimePreset) {
      this.initializeCustomDates();
    }
  }

  /**
   * Initialize custom date range with default values (1 week ago to today)
   */
  private initializeCustomDateRange(): void {
    const today = new Date();
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(today.getDate() - 7);

    // Set the Date objects for the date pickers
    this.endDate = today;
    this.startDate = oneWeekAgo;

    // Set the string values in filters for the API
    this.filters.startDate = oneWeekAgo.toISOString().split('T')[0];
    this.filters.endDate = today.toISOString().split('T')[0];

    // Mark that custom dates have been initialized
    this.customDatesInitialized = true;
  }

  onHierarchicalFiltersChange(params: FilterParams): void {
    // Update filters based on hierarchical filter changes
    // Create a new object reference to trigger change detection
    // Handle both single values and arrays for multi-select support
    this.filters = {
      ...this.filters,
      distributors: this.normalizeFilterArray(params.disti_id),
      markets: this.normalizeFilterArray(params.market_id),
      clients: this.normalizeFilterArray(params.client_id),
      campuses: this.normalizeFilterArray(params.campus_id),
      machines: this.normalizeFilterArray(params.machine_id)
    };
    
    // Load hierarchical filter options for export metadata
    this.loadHierarchicalFilterOptions();
  }
  
  /**
   * Load hierarchical filter options for export metadata
   */
  private loadHierarchicalFilterOptions(): void {
    // Load distributors
    if (this.role === 'sadmin') {
      this.filterOptionsService.loadDistributors(this.role).subscribe(options => {
        this.distributorOptions = options;
      });
    }
    
    // Load markets
    if (this.filters.distributors && this.filters.distributors.length > 0) {
      this.filterOptionsService.loadMarkets(this.filters.distributors).subscribe(options => {
        this.marketOptions = options;
      });
    } else {
      this.marketOptions = [];
    }
    
    // Load clients
    this.filterOptionsService.loadClients(
      this.filters.distributors.length > 0 ? this.filters.distributors : null,
      this.filters.markets.length > 0 ? this.filters.markets : null
    ).subscribe(options => {
      this.clientOptions = options;
    });
    
    // Load campuses
    this.filterOptionsService.loadCampuses(
      this.filters.clients.length > 0 ? this.filters.clients : null,
      this.filters.distributors.length > 0 ? this.filters.distributors : null,
      this.filters.markets.length > 0 ? this.filters.markets : null
    ).subscribe(options => {
      this.campusOptions = options;
    });
    
    // Load machines
    this.filterOptionsService.loadMachines(
      this.filters.campuses.length > 0 ? this.filters.campuses : null,
      this.filters.clients.length > 0 ? this.filters.clients : null,
      this.filters.distributors.length > 0 ? this.filters.distributors : null,
      this.filters.markets.length > 0 ? this.filters.markets : null
    ).subscribe(options => {
      this.machineOptions = options;
    });
  }

  /**
   * Normalize filter parameter to string array
   */
  private normalizeFilterArray(value: string | number | (string | number)[] | undefined): string[] {
    if (!value) {
      return [];
    }
    if (Array.isArray(value)) {
      return value.map(id => String(id));
    }
    return [String(value)];
  }

  setActiveTab(tab: TabKey) {
    this.activeTab = tab;
    
    // Track all tab clicks
    this.mixpanelService.track('Analytics Tab Click', {
      tab: tab
    });
    
    if (tab === 'drink-volume' && !this.allowedBreakdowns.includes(this.filters.breakdownBy)) {
      this.filters.breakdownBy = 'drink';
    } else if (tab === 'consumption' && !this.allowedBreakdowns.includes(this.filters.breakdownBy)) {
      this.filters.breakdownBy = 'product';
    } else if (tab === 'forecast' && !this.allowedBreakdowns.includes(this.filters.breakdownBy)) {
      this.filters.breakdownBy = 'product';
    }

    // Initialize custom dates if switching to a tab that shows time preset and datePreset is custom
    if (this.filters.datePreset === 'custom' && this.showTimePreset) {
      // Only initialize if dates are not already set
      if (!this.filters.startDate || !this.filters.endDate) {
        this.initializeCustomDates();
      }
    }
  }

  setBreakdownBy(value: FilterState['breakdownBy']) {
    this.filters.breakdownBy = value;
  }

  setSaleScope(value: FilterState['saleScope']) {
    this.filters.saleScope = value;
  }

  setAverage(value: 'off' | 'day' | 'week' | 'month') {
    if (value === 'off') {
      this.filters.averageEnabled = false;
      return;
    }

    if (!this.isAverageOptionAllowed(value)) {
      this.filters.averageEnabled = false;
      return;
    }

    this.filters.averageEnabled = true;
    this.filters.avgBy = value;
  }

  onCompareToggleChange(): void {
    // Drink Volume tab handles its own compare toggle
    // Consumption tab handles its own compare toggle
  }

  setDatePreset(value: '7' | '30' | '90' | '365' | 'custom') {
    if (value === 'custom') {
      this.filters.datePreset = 'custom';
      // Initialize dates for drink-volume and consumption tabs
      if (this.showTimePreset) {
        this.initializeCustomDates();
      }
    } else {
      const days = Number(value);
      this.filters.datePreset = value;
      this.filters.days = Number.isFinite(days) ? days : this.filters.days;
      // Clear custom dates when switching to preset
      this.filters.startDate = undefined;
      this.filters.endDate = undefined;
      this.startDate = null;
      this.endDate = null;
    }

    this.ensureAverageCompatibility();
  }

  /**
   * Initialize custom date range: start date = 7 days ago, end date = today
   * Only initializes if dates are not already set
   */
  private initializeCustomDates(): void {
    // Don't overwrite if dates are already set
    if (this.filters.startDate && this.filters.endDate && this.startDate && this.endDate) {
      return;
    }

    const today = new Date();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(today.getDate() - 7);

    // Set time to start of day for both dates
    today.setHours(0, 0, 0, 0);
    sevenDaysAgo.setHours(0, 0, 0, 0);

    this.startDate = sevenDaysAgo;
    this.endDate = today;
    this.syncDatesToFilters();
  }

  onStartDateChange(event: { value: Date | null }): void {
    this.startDate = event.value;

    // If start date is after end date, clear end date
    if (this.startDate && this.endDate && this.startDate > this.endDate) {
      this.endDate = null;
    }

    this.syncDatesToFilters();
  }

  onEndDateChange(event: { value: Date | null }): void {
    this.endDate = event.value;

    // If end date is before start date, clear start date
    if (this.startDate && this.endDate && this.endDate < this.startDate) {
      this.startDate = null;
    }

    this.syncDatesToFilters();
  }

  private syncDatesToFilters(): void {
    // Create a new filters object to trigger change detection
    this.filters = {
      ...this.filters,
      startDate: this.startDate ? this.startDate.toISOString().split('T')[0] : undefined,
      endDate: this.endDate ? this.endDate.toISOString().split('T')[0] : undefined
    };

    this.ensureAverageCompatibility();
  }

  onDrinkSelectionChange(selected: (string | number)[] | null): void {
    if (selected === null || (Array.isArray(selected) && selected.length === 0)) {
      this.filters.drinks = [];
    } else if (Array.isArray(selected)) {
      this.filters.drinks = selected.map(v => String(v));
    } else {
      this.filters.drinks = [String(selected)];
    }
  }

  onProductSelectionChange(selected: (string | number)[] | null): void {
    if (selected === null || (Array.isArray(selected) && selected.length === 0)) {
      this.filters.products = [];
    } else if (Array.isArray(selected)) {
      this.filters.products = selected.map(v => String(v));
    } else {
      this.filters.products = [String(selected)];
    }
  }

  /**
   * Getter for export button disabled state
   * Disable only if ViewChild is not available
   * Enable as soon as ViewChild is available (export function handles empty data gracefully)
   */
  get isExportDisabled(): boolean {
    let disabled = false;
    let reason = '';
    
    if (this.activeTab === 'drink-volume') {
      // Disable only if ViewChild not available
      if (!this.drinkVolumeTab) {
        disabled = true;
        reason = 'drinkVolumeTab ViewChild not available';
      } else {
        // Enable as soon as ViewChild is available
        console.log(`[Analytics Export] Export button enabled for drink-volume (isLoading: ${this.drinkVolumeTab.isLoadingDrinkVolume}, hasData: ${this.drinkVolumeTab.hasData()})`);
      }
    } else if (this.activeTab === 'consumption') {
      if (!this.consumptionTab) {
        disabled = true;
        reason = 'consumptionTab ViewChild not available';
      } else {
        console.log(`[Analytics Export] Export button enabled for consumption (isLoading: ${this.consumptionTab.isLoadingConsumption}, hasData: ${this.consumptionTab.hasData()})`);
      }
    } else if (this.activeTab === 'forecast') {
      if (!this.forecastTab) {
        disabled = true;
        reason = 'forecastTab ViewChild not available';
      } else {
        // Enable as soon as ViewChild is available
        const hasData = this.forecastTab.hasData();
        const isLoadingForecastConsumption = this.forecastTab.isLoadingForecastConsumption;
        const isLoadingForecastConsumptionOverTime = this.forecastTab.isLoadingForecastConsumptionOverTime !== undefined ? this.forecastTab.isLoadingForecastConsumptionOverTime : false;
        console.log(`[Analytics Export] Export button enabled for forecast (isLoadingForecastConsumption: ${isLoadingForecastConsumption}, isLoadingForecastConsumptionOverTime: ${isLoadingForecastConsumptionOverTime}, hasData: ${hasData})`);
      }
    } else if (this.activeTab === 'stockouts') {
      if (!this.stockoutsTab) {
        disabled = true;
        reason = 'stockoutsTab ViewChild not available';
      } else {
        const hasData = this.stockoutsTab.hasData();
        const isLoading = this.stockoutsTab.isLoadingStockoutRisk;
        const stockoutRowsLength = this.stockoutsTab.stockoutRows ? this.stockoutsTab.stockoutRows.length : 0;
        console.log(`[Analytics Export] Export button enabled for stockouts (isLoading: ${isLoading}, hasData: ${hasData}, stockoutRowsLength: ${stockoutRowsLength})`);
      }
    } else {
      disabled = true;
      reason = `unknown activeTab: ${this.activeTab}`;
    }
    
    if (disabled) {
      console.log('[Analytics Export] Export button disabled:', reason);
    }
    
    return disabled;
  }

  /**
   * Handle export button click
   */
  onExport(): void {
    console.log('[Analytics Export] Export button clicked');
    console.log('[Analytics Export] Active tab:', this.activeTab);
    console.log('[Analytics Export] ViewChild references:', {
      drinkVolumeTab: !!this.drinkVolumeTab,
      consumptionTab: !!this.consumptionTab,
      forecastTab: !!this.forecastTab,
      stockoutsTab: !!this.stockoutsTab
    });
    
    try {
      switch (this.activeTab) {
        case 'drink-volume':
          console.log('[Analytics Export] Processing drink-volume tab export');
          if (this.drinkVolumeTab) {
            console.log('[Analytics Export] Drink volume tab component found, calling exportTable()');
            try {
              console.log('[Analytics Export] Drink volume tab state:', {
                isLoading: this.drinkVolumeTab.isLoadingDrinkVolume,
                hasData: this.drinkVolumeTab.hasData()
              });
            } catch (e) {
              console.warn('[Analytics Export] Could not access drink volume tab properties:', e);
            }
            this.drinkVolumeTab.exportTable();
          } else {
            console.warn('[Analytics Export] Drink volume tab component not available');
            this.snackBar.open('Please wait for data to load before exporting.', 'Dismiss', { duration: 3000 });
          }
          break;
        case 'consumption':
          console.log('[Analytics Export] Processing consumption tab export');
          if (this.consumptionTab) {
            console.log('[Analytics Export] Consumption tab component found, calling exportTable()');
            console.log('[Analytics Export] Consumption tab state:', {
              isLoading: this.consumptionTab.isLoadingConsumption,
              hasData: this.consumptionTab.hasData()
            });
            this.consumptionTab.exportTable();
          } else {
            console.warn('[Analytics Export] Consumption tab component not available');
            this.snackBar.open('Please wait for data to load before exporting.', 'Dismiss', { duration: 3000 });
          }
          break;
        case 'forecast':
          console.log('[Analytics Export] Processing forecast tab export');
          if (this.forecastTab) {
            console.log('[Analytics Export] Forecast tab component found, calling exportTable()');
            console.log('[Analytics Export] Forecast tab state:', {
              isLoading: this.forecastTab.isLoadingForecastConsumption,
              hasData: this.forecastTab.hasData()
            });
            this.forecastTab.exportTable();
          } else {
            console.warn('[Analytics Export] Forecast tab component not available');
            this.snackBar.open('Please wait for data to load before exporting.', 'Dismiss', { duration: 3000 });
          }
          break;
        case 'stockouts':
          console.log('[Analytics Export] Processing stockouts tab export');
          if (this.stockoutsTab) {
            console.log('[Analytics Export] Stockouts tab component found, calling exportTable()');
            console.log('[Analytics Export] Stockouts tab state:', {
              isLoading: this.stockoutsTab.isLoadingStockoutRisk,
              hasData: this.stockoutsTab.hasData()
            });
            this.stockoutsTab.exportTable();
          } else {
            console.warn('[Analytics Export] Stockouts tab component not available');
            this.snackBar.open('Please wait for data to load before exporting.', 'Dismiss', { duration: 3000 });
          }
          break;
        default:
          console.warn('[Analytics Export] Unknown active tab:', this.activeTab);
      }
    } catch (error) {
      console.error('[Analytics Export] Export error:', error);
      this.snackBar.open('Failed to export data. Please try again.', 'Dismiss', { duration: 5000 });
    }
  }

  setForecastDays(days: number) {
    this.forecastDays = days;
  }

  onMachineFilterChange(machines: string[]): void {
    // Create a new object reference to trigger change detection
    this.filters = {
      ...this.filters,
      machines: machines
    };
  }

  /**
   * Load available recipes/drinks from API
   */
  loadRecipes(): void {
    this.userSettingsHttpService.getRecipes().subscribe(
      (response: unknown) => {
        const recipes = this.extractRecipesFromResponse(response);

        if (recipes && recipes.length > 0) {
          this.drinkOptions = recipes
            .filter((recipe: Recipe) => recipe.recipeName && recipe.recipeId)
            .map((recipe: Recipe) => ({
              value: recipe.recipeId,
              label: capitalizeRecipeName(recipe.recipeName)
            }));

          this.ALL_DRINKS = this.drinkOptions.map(option => option.label);
        } else {
          console.warn('No recipes found in API response, using default list');
          this.setDefaultDrinkOptions();
        }
      },
      (error: unknown) => {
        console.error('Error loading recipes:', error);
        this.setDefaultDrinkOptions();
      }
    );
  }

  /**
   * Extract recipes array from API response
   */
  private extractRecipesFromResponse(response: unknown): Recipe[] {
    if (!response || typeof response !== 'object') {
      return [];
    }

    const responseObj = response as Record<string, unknown>;

    if (responseObj.data && Array.isArray(responseObj.data)) {
      return responseObj.data as Recipe[];
    }

    if (Array.isArray(response)) {
      return response as Recipe[];
    }

    return [];
  }

  /**
   * Set default drink options from ALL_DRINKS
   */
  private setDefaultDrinkOptions(): void {
    this.drinkOptions = this.ALL_DRINKS.map((drink: string) => ({
      value: drink,
      label: drink
    }));
  }

  private isAverageOptionAllowed(value: 'day' | 'week' | 'month'): boolean {
    if (value === 'day') {
      return true;
    }

    const rangeDays = this.currentRangeDays;

    if (!rangeDays) {
      return false;
    }

    return rangeDays >= AVERAGE_RANGE_REQUIREMENTS[value];
  }

  private ensureAverageCompatibility(): void {
    if (!this.filters.averageEnabled) {
      return;
    }

    if (this.isAverageOptionAllowed(this.filters.avgBy)) {
      return;
    }

    this.filters.averageEnabled = false;
  }

  private getCustomRangeDays(): number | null {
    if (!this.filters.startDate || !this.filters.endDate) {
      return null;
    }

    const start = new Date(this.filters.startDate);
    const end = new Date(this.filters.endDate);

    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return null;
    }

    const diff = end.getTime() - start.getTime();

    if (diff < 0) {
      return null;
    }

    return Math.floor(diff / MS_PER_DAY) + 1;
  }

  /**
   * Load available products from API
   */
  loadProducts(): void {
    this.userSettingsHttpService.getProducts().subscribe(
      (response: unknown) => {
        const products = this.extractProductsFromResponse(response);

        if (products && products.length > 0) {
          this.productOptions = products
            .filter((product: Product) => product.prodName && product.prodId)
            .filter((product: Product) => {
              // Exclude Oatmilk as it's redundant with Oat
              const normalizedName = product.prodName.toUpperCase();
              return normalizedName !== 'OATMILK';
            })
            .map((product: Product) => ({
              value: product.prodId,
              label: formatProductNameUtil(product.prodName)
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
        } else {
          console.warn('No products found in API response');
          this.productOptions = [];
        }
      },
      (error: unknown) => {
        console.error('Error loading products:', error);
        this.productOptions = [];
      }
    );
  }

  /**
   * Extract products array from API response
   */
  private extractProductsFromResponse(response: unknown): Product[] {
    if (!response || typeof response !== 'object') {
      return [];
    }

    const responseObj = response as Record<string, unknown>;

    if (responseObj.data && Array.isArray(responseObj.data)) {
      return responseObj.data as Product[];
    }

    if (Array.isArray(response)) {
      return response as Product[];
    }

    return [];
  }

}
